import React, {
  ChangeEventHandler,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import {
  Button,
  Col,
  ConfigProvider,
  DatePicker,
  Dropdown,
  Input,
  MenuProps,
  Modal,
  Progress,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  theme,
} from "antd";
import type { DatePickerProps, RangePickerProps } from "antd/es/date-picker";
import type { ColumnType, ColumnsType } from "antd/es/table";
import {
  RequestDataQuery,
  File,
  Folder,
  DownloadProgressEvent,
  FilterField,
  Comparison,
  Field,
  ContainerData,
  ContainersList,
  FileTrackData,
  FailureFileTrackData,
} from "../../../api/data-types";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  FileOutlined,
  FilterFilled,
  FolderOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import "./file-browser.css";
import { StorageAPIClient } from "../../../api/storage-api-client";
import { TableRowSelection } from "antd/es/table/interface";
import { DropdownListSelection } from "../list-selection-dropdown/list-selection-dropdown";
import dayjs from "dayjs";
import { Dayjs } from "dayjs";
import {
  useAppInsightsContext,
  useTrackEvent,
} from "@microsoft/applicationinsights-react-js";
import { isLocalHost } from "../../../util/helpers";
import { ClientApiDiagnosticEvents } from "../../../util/diagnosticEvent";

export interface SizeWithUnit {
  size: number;
  unit: string;
}
interface FileBrowsingTableProps {
  selectedSearchType: string;
  theme: string;
  filterOn: boolean;
  dataQuerry: RequestDataQuery;
  onFolderClick: (name: string) => void;
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: (selectedRowKeys: React.Key[]) => void;
  showFolderColumn: boolean;
  showSelectFolderAction: boolean;
  onSelectFolderClick: (name: string) => void;
  continuationTokenNavigation: (token: string) => Promise<void>;
  previousTokens: string[];
  ySizeVH: string;
  sendFilterAndSorting: (
    sortField: Field,
    filterFields: FilterField[],
    isAscending: boolean,
    sizesWithUnit: SizeWithUnit[]
  ) => void;
  filterFieldsFromParent: FilterField[];
  sizesWithUnitFromParent: SizeWithUnit[];
}

export const FileBrowsingTable = ({
  selectedSearchType,
  theme,
  filterOn,
  dataQuerry,
  onFolderClick,
  selectedRowKeys,
  setSelectedRowKeys,
  showFolderColumn,
  showSelectFolderAction,
  onSelectFolderClick,
  continuationTokenNavigation,
  previousTokens,
  ySizeVH,
  sendFilterAndSorting,
  filterFieldsFromParent,
  sizesWithUnitFromParent,
}: FileBrowsingTableProps) => {
  interface DataType {
    key: string;
    name: string;
    size: string;
    modificationDate: string;
    folder?: string;
    isFolder: boolean;
  }

  const [disable, setDisable] = useState<boolean>(false);

  const [height, setHeight] = useState<string>();

  const [percentDone, setPercentDone] = useState<number>(0);

  const [downloading, setDownloading] = useState<boolean>(false);

  const [avgSpeed, setAvgSpeed] = useState<number | undefined>();

  const textColor = theme === "light" ? "#000000" : "#ffffff";

  const sizeItems = ["B", "KB", "MB", "GB", "TB"];

  const sizeRangeItems = [">", ">=", "=", "<=", "<"];

  const [isFilteringVisible, setIsFilteringVisible] = useState<boolean>();

  const [filterFields, setFilterFields] = useState<FilterField[]>(
    filterFieldsFromParent
  );

  const [sizesWithUnit, setSizesWithUnit] = useState<SizeWithUnit[]>(
    sizesWithUnitFromParent
  );

  let isAscending: boolean = true;
  let sortField: Field = Field.FileName;

  const comparisonToSign = (comparison: Comparison): string => {
    switch (comparison) {
      case Comparison.GreaterThan:
        return ">";
      case Comparison.GreaterThanOrEqual:
        return ">=";
      case Comparison.Equals:
        return "=";
      case Comparison.LowerThanOrEqual:
        return "<=";
      case Comparison.LowerThan:
        return "<";
      default:
        return "=";
    }
  };

  const sizeWithUnitToSize = (size: number, unit: string): number => {
    switch (unit) {
      case "B":
        return size;
      case "KB":
        return size * 1024;
      case "MB":
        return size * 1048576;
      case "GB":
        return size * 1073741824;
      case "TB":
        return size * 1099511627776;
      default:
        return 0;
    }
  };

  const handleRangeChange = (selectedValue: string, index: number) => {
    const newSizeRange = selectedValue;
    let comparisonChosen: Comparison;

    switch (newSizeRange) {
      case ">":
        comparisonChosen = Comparison.GreaterThan;
        break;
      case ">=":
        comparisonChosen = Comparison.GreaterThanOrEqual;
        break;
      case "=":
        comparisonChosen = Comparison.Equals;
        break;
      case "<=":
        comparisonChosen = Comparison.LowerThanOrEqual;
        break;
      case "<":
        comparisonChosen = Comparison.LowerThan;
        break;
      default:
        comparisonChosen = Comparison.Equals;
        break;
    }
    let newTable = filterFields;
    let newElement = filterFields[index];
    newElement.comparison = comparisonChosen;
    newTable[index] = newElement;
    setFilterFields(newTable);
  };

  const handleSizeChange = (selectedValue: string, index: number) => {
    let newSizeWithUnit: SizeWithUnit = {
      size: sizesWithUnit[index - 2].size,
      unit: selectedValue,
    };
    let newSizes = sizesWithUnit;
    newSizes[index - 2] = newSizeWithUnit;
    setSizesWithUnit(newSizes);
    let sizeForFilter = sizeWithUnitToSize(
      sizesWithUnit[index - 2].size,
      selectedValue
    );
    let newTable = filterFields;
    let newElement = filterFields[index];
    newElement.valueOrPattern = sizeForFilter.toString();
    newTable[index] = newElement;
    setFilterFields(newTable);
  };

  const handleAddSize = (event: any, index: number) => {
    const newSize = event.target.value;
    let newSizeWithUnit: SizeWithUnit = {
      size: parseInt(newSize),
      unit: sizesWithUnit[index - 2].unit,
    };
    let newSizes = sizesWithUnit;
    newSizes[index - 2] = newSizeWithUnit;
    setSizesWithUnit(newSizes);
    let newTable = filterFields;
    let newElement = filterFields[index];
    newElement.valueOrPattern = sizeWithUnitToSize(
      newSize,
      sizesWithUnit[index - 2].unit
    ).toString();
    newTable[index] = newElement;
    setFilterFields(newTable);
  };

  const handleAddValueOrPattern = (label: string, index: number) => {
    const newValue = label;
    let newTable = filterFields;
    let newElement = filterFields[index];
    newElement.valueOrPattern = newValue;
    newTable[index] = newElement;
    setFilterFields(newTable);
  };

  const handleOKFilter = (
    filterFields: FilterField[],
    sizesWithUnit: SizeWithUnit[]
  ) => {
    sendFilterAndSorting(sortField, filterFields, isAscending, sizesWithUnit);
  };

  const handleSorting = (selectedField: Field, AscOrDesc: boolean) => {
    sortField = selectedField;
    isAscending = AscOrDesc;
    handleOKFilter(filterFields, sizesWithUnit);
  };

  const handleResetFiltering = (index: number[]) => {
    let newTable: FilterField[] = [];
    let newSizeTable: SizeWithUnit[] = sizesWithUnit;

    for (let i = 0; i < index.length; i++) {
      let indexOfValue = index[i];
      let newElement: FilterField = new FilterField(
        Field.FileName,
        Comparison.Like,
        ""
      );
      let newSizeWithUnit: SizeWithUnit = { size: 0, unit: "B" };

      switch (indexOfValue) {
        case 0:
          newElement = new FilterField(Field.FileName, Comparison.Like, "");
          break;
        case 1:
          newElement = new FilterField(Field.FolderName, Comparison.Like, "");
          break;
        case 2:
          newElement = new FilterField(Field.FileSize, Comparison.Equals, "");
          newSizeTable[0] = newSizeWithUnit;
          break;
        case 3:
          newElement = new FilterField(Field.FileSize, Comparison.Equals, "");
          newSizeTable[1] = newSizeWithUnit;
          break;
        case 4:
          newElement = new FilterField(
            Field.ModificationDate,
            Comparison.Equals,
            ""
          );
          break;
        case 5:
          newElement = new FilterField(
            Field.ModificationDate,
            Comparison.Equals,
            ""
          );
          break;
      }
      newTable = filterFields;
      let newValue = filterFields[index[i]];
      newValue = newElement;
      newTable[index[i]] = newValue;
    }
    setSizesWithUnit(newSizeTable);
    setFilterFields(newTable);
    handleOKFilter(newTable, newSizeTable);
  };

  useEffect(() => {
    const handleResize = () => {
      setHeight(ySizeVH);
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setIsFilteringVisible(
      filterOn &&
        (selectedSearchType === "Metadata" ||
          selectedSearchType === "Folder pattern")
    );
  }, [filterOn, selectedSearchType]);

  const onProgress = (ev: DownloadProgressEvent) => {
    setPercentDone(Math.round((ev.loaded / ev.fileSize) * 100));
    if (ev.rate) {
      setAvgSpeed(ev.rate);
    }
  };

  const handleOkDownload = () => {
    setPercentDone(0);
    setAvgSpeed(undefined);
  };

  const handlePagingClick = async (token: string) => {
    setDisable(true);
    await continuationTokenNavigation(token);
    setDisable(false);
  };

  const handleFolderClick = (folder: Folder) => {
    onFolderClick(folder.name);
  };

  const handleSelectFolder = (folder: Folder) => {
    onSelectFolderClick(folder.name);
  };

  const appInsights = useAppInsightsContext();
  const trackDownloadEvent = useTrackEvent(
    appInsights,
    ClientApiDiagnosticEvents[ClientApiDiagnosticEvents.ClientDownloadFile],
    {} as FileTrackData
  );
  const trackDownloadFailureEvent = useTrackEvent(
    appInsights,
    ClientApiDiagnosticEvents[
      ClientApiDiagnosticEvents.ClientDownloadFileFailure
    ],
    {} as FailureFileTrackData
  );
  const getFileTrackedEvent = (
    file: File,
    start: Date,
    containerName: string
  ) => {
    const end = new Date();
    const totalTime = end.getTime() - start.getTime();
    const fileTracked = {
      totalTime,
      containerName,
      folderName: file.folderName,
      fileName: file.name,
      fileGuid: file.fileGuid,
      fileSize: file.fileSize,
    };
    return fileTracked;
  };

  const handleDownloadClick = async (
    file: File,
    containerName: string,
    parentFolder?: string
  ) => {
    setDownloading(true);
    const start = new Date();
    try {
      const newHandle = await window.showSaveFilePicker({
        suggestedName: file.name,
      });
      const writableStream = await newHandle.createWritable();
      await StorageAPIClient.downloadFile(
        containerName,
        file,
        file.fileGuid,
        onProgress,
        writableStream,
        parentFolder
      ).then(() => {
        setDownloading(false);
        if (!isLocalHost()) {
          const fileTracked = getFileTrackedEvent(file, start, containerName);
          trackDownloadEvent(fileTracked);
        }
      });
    } catch (error: any) {
      const fileTracked = getFileTrackedEvent(file, start, containerName);
      trackDownloadFailureEvent({ ...fileTracked, error });
      console.log(error.message);
      setDownloading(false);
    }
  };

  const sizeHumanReadable = (size: number): string => {
    let i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
    return (
      Math.round(size / Math.pow(1024, i)) +
      " " +
      ["B", "KB", "MB", "GB", "TB"][i]
    );
  };

  const handleOnCellClick = (record: any) => {
    if (record.isFolder) {
      handleFolderClick(record as Folder);
    } else {
      if (!selectedRowKeys.includes(record.key as React.Key)) {
        rowSelectionProps.selectedRowKeys = [
          ...selectedRowKeys,
          record.key as React.Key,
        ];
        setSelectedRowKeys([...selectedRowKeys, record.key as React.Key]);
      } else {
        rowSelectionProps.selectedRowKeys = selectedRowKeys.filter(
          (value) => value !== (record.key as React.Key)
        );
        setSelectedRowKeys(
          selectedRowKeys.filter((value) => value !== (record.key as React.Key))
        );
      }
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: isFilteringVisible ? (
        <Row>
          <Col span={23} className="columnName">
            <p>Name</p>
          </Col>
          <Col span={1} style={{ paddingTop: "7px" }}>
            <Row>
              <CaretUpOutlined
                className="sortButton"
                onClick={() => handleSorting(Field.FileName, true)}
              />
            </Row>
            <Row>
              <CaretDownOutlined
                className="sortButton"
                onClick={() => handleSorting(Field.FileName, false)}
              />
            </Row>
          </Col>
        </Row>
      ) : (
        <Col span={23}>
          <p>Name</p>
        </Col>
      ),
      dataIndex: "name",
      key: "name",
      onCell: (record) => {
        return {
          onClick: () => handleOnCellClick(record),
        };
      },
      filterDropdown: () => {
        if (isFilteringVisible) {
          return (
            <div style={{ padding: 8 }}>
              <Space direction="vertical" size={16}>
                <Row>
                  <Col>
                    <Tooltip title={'The "*" sign represents any character'}>
                      <Input
                        style={{ color: textColor }}
                        onChange={(event) =>
                          handleAddValueOrPattern(event.target.value, 0)
                        }
                        placeholder="Filter by file name"
                        defaultValue={filterFields[0].valueOrPattern}
                      />
                    </Tooltip>
                  </Col>
                </Row>
                <Row>
                  <Col span={10}>
                    <Button
                      className="filterButton"
                      onClick={() => handleResetFiltering([0])}
                    >
                      Reset
                    </Button>
                  </Col>
                  <Col span={14} className="filterOkButtonColumn">
                    <Button
                      className="filterButton"
                      onClick={() =>
                        handleOKFilter(filterFields, sizesWithUnit)
                      }
                    >
                      Filter
                    </Button>
                  </Col>
                </Row>
              </Space>
            </div>
          );
        } else {
          return <></>;
        }
      },
      filterIcon: () => (
        <Button
          type="link"
          icon={<FilterFilled />}
          className={
            isFilteringVisible ? "filter-icon-visible" : "filter-icon-hidden"
          }
        />
      ),
      render: (text: string, record: DataType) => (
        <Row>
          <Col span={3} style={{ minWidth: "15px" }}>
            {record.isFolder ? <FolderOutlined /> : <FileOutlined />}
          </Col>
          <Col span={21}>
            {record.isFolder ? <div className="record">{text}</div> : text}
          </Col>
        </Row>
      ),
    },
    {
      title: isFilteringVisible ? (
        <Row>
          <Col span={23} className="columnName">
            <p>Size</p>
          </Col>
          <Col span={1} style={{ paddingTop: "7px" }}>
            <Row>
              <CaretUpOutlined
                className="sortButton"
                onClick={() => handleSorting(Field.FileSize, true)}
              />
            </Row>
            <Row>
              <CaretDownOutlined
                className="sortButton"
                onClick={() => handleSorting(Field.FileSize, false)}
              />
            </Row>
          </Col>
        </Row>
      ) : (
        <Col span={23}>
          <p>Size</p>
        </Col>
      ),
      dataIndex: "size",
      key: "size",
      width: "16%",
      align: "center",
      filterDropdown: () => {
        if (isFilteringVisible) {
          return (
            <div style={{ padding: 8 }}>
              <Space direction="vertical" size={16}>
                <Row>
                  <Col span={7}>
                    <DropdownListSelection
                      theme={theme}
                      optionsList={sizeRangeItems}
                      onMenuClick={(label) => handleRangeChange(label, 2)}
                      placeholderText={comparisonToSign(
                        filterFields[2].comparison
                      )}
                    />
                  </Col>
                  <Col span={11}>
                    <Input
                      style={{ color: textColor }}
                      onChange={(event) => handleAddSize(event, 2)}
                      placeholder="Value"
                      defaultValue={
                        sizesWithUnit[0].size === 0
                          ? ""
                          : sizesWithUnit[0].size.toString()
                      }
                    ></Input>
                  </Col>
                  <Col span={6} className="sizeDropdownColumn">
                    <DropdownListSelection
                      theme={theme}
                      optionsList={sizeItems}
                      onMenuClick={(label) => handleSizeChange(label, 2)}
                      placeholderText={sizesWithUnit[0].unit}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={7} className="dropdownButtonColumn">
                    <DropdownListSelection
                      theme={theme}
                      optionsList={sizeRangeItems}
                      onMenuClick={(label) => handleRangeChange(label, 3)}
                      placeholderText={comparisonToSign(
                        filterFields[3].comparison
                      )}
                    />
                  </Col>
                  <Col span={11}>
                    <Input
                      style={{ color: textColor }}
                      onChange={(event) => handleAddSize(event, 3)}
                      placeholder="Value"
                      defaultValue={
                        sizesWithUnit[1].size === 0
                          ? ""
                          : sizesWithUnit[1].size.toString()
                      }
                    ></Input>
                  </Col>
                  <Col span={6} className="sizeDropdownColumn">
                    <DropdownListSelection
                      theme={theme}
                      optionsList={sizeItems}
                      onMenuClick={(label) => handleSizeChange(label, 3)}
                      placeholderText={sizesWithUnit[1].unit}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={7}>
                    <Button
                      className="filterResetSizeButton"
                      onClick={() => handleResetFiltering([2, 3])}
                    >
                      Reset
                    </Button>
                  </Col>
                  <Col span={17} className="filterOkButtonColumn">
                    <Button
                      className="filterButton"
                      onClick={() =>
                        handleOKFilter(filterFields, sizesWithUnit)
                      }
                    >
                      Filter
                    </Button>
                  </Col>
                </Row>
              </Space>
            </div>
          );
        } else {
          return <></>;
        }
      },
      filterIcon: () => (
        <Button
          type="link"
          icon={<FilterFilled />}
          className={
            isFilteringVisible ? "filter-icon-visible" : "filter-icon-hidden"
          }
        />
      ),
      onCell: (record) => {
        return {
          onClick: () => handleOnCellClick(record),
        };
      },
    },
    {
      title: isFilteringVisible ? (
        <Row>
          <Col span={23} className="columnName">
            <p>Last modified</p>
          </Col>
          <Col span={1} style={{ paddingTop: "7px" }}>
            <Row>
              <CaretUpOutlined
                className="sortButton"
                onClick={() => handleSorting(Field.ModificationDate, true)}
              />
            </Row>
            <Row>
              <CaretDownOutlined
                className="sortButton"
                onClick={() => handleSorting(Field.ModificationDate, false)}
              />
            </Row>
          </Col>
        </Row>
      ) : (
        <Col span={23}>
          <p>Last modified</p>
        </Col>
      ),
      dataIndex: "modificationDate",
      key: "modificationDate",
      width: "23%",
      align: "center",
      onCell: (record) => {
        return {
          onClick: () => handleOnCellClick(record),
        };
      },
      filterIcon: () => (
        <Button
          type="link"
          icon={<FilterFilled />}
          className={
            isFilteringVisible ? "filter-icon-visible" : "filter-icon-hidden"
          }
        />
      ),
      filterDropdown: () => {
        if (isFilteringVisible) {
          return (
            <div style={{ padding: 8 }}>
              <Space direction="vertical" size={12}>
                <Row>
                  <Col>
                    <DropdownListSelection
                      theme={theme}
                      optionsList={sizeRangeItems}
                      onMenuClick={(label) => handleRangeChange(label, 4)}
                      placeholderText={comparisonToSign(
                        filterFields[4].comparison
                      )}
                    />
                  </Col>
                  <Col>
                    <DatePicker
                      showTime
                      onChange={(value, label) =>
                        handleAddValueOrPattern(label.toString(), 4)
                      }
                      defaultValue={
                        filterFields[4].valueOrPattern === ""
                          ? undefined
                          : dayjs(filterFields[4].valueOrPattern)
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <DropdownListSelection
                      theme={theme}
                      optionsList={sizeRangeItems}
                      onMenuClick={(label) => handleRangeChange(label, 5)}
                      placeholderText={comparisonToSign(
                        filterFields[5].comparison
                      )}
                    />
                  </Col>
                  <Col>
                    <DatePicker
                      showTime
                      onChange={(value, label) =>
                        handleAddValueOrPattern(label.toString(), 5)
                      }
                      defaultValue={
                        filterFields[5].valueOrPattern === ""
                          ? undefined
                          : dayjs(filterFields[5].valueOrPattern)
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={12}>
                    <Button
                      className="filterButton"
                      onClick={() => handleResetFiltering([4, 5])}
                    >
                      Reset
                    </Button>
                  </Col>
                  <Col span={12} className="filterOkButtonColumn">
                    <Button
                      className="filterButton"
                      onClick={() =>
                        handleOKFilter(filterFields, sizesWithUnit)
                      }
                    >
                      Filter
                    </Button>
                  </Col>
                </Row>
              </Space>
            </div>
          );
        } else {
          return <></>;
        }
      },
    },
    {
      title: <Col>Action</Col>,
      key: "action",
      width: "20%",
      align: "center",
      render: (record: DataType) => (
        <Space size="middle">
          {record.isFolder ? (
            <>
              <Button
                className="OpenDownload"
                type="text"
                onClick={() => handleFolderClick(record as Folder)}
                style={{
                  background: "transparent",
                }}
              >
                Open
              </Button>
              {showSelectFolderAction && (
                <Button
                  className="OpenDownload"
                  type="text"
                  onClick={() => handleSelectFolder(record as Folder)}
                  style={{
                    background: "transparent",
                  }}
                >
                  Select folder
                </Button>
              )}
            </>
          ) : (
            <Button
              className="OpenDownload"
              type="text"
              onClick={() =>
                handleDownloadClick(
                  dataQuerry.files[Number(record.key)],
                  dataQuerry.containerName,
                  dataQuerry.files[Number(record.key)].folderName
                )
              }
              style={{
                background: "transparent",
              }}
            >
              Download
            </Button>
          )}
        </Space>
      ),
    },
  ];

  if (showFolderColumn) {
    const pathColumn: ColumnType<DataType> = {
      title: isFilteringVisible ? (
        <Row>
          <Col span={23} className="columnName">
            <p>Folder</p>
          </Col>
          <Col span={1} style={{ paddingTop: "7px" }}>
            <Row>
              <CaretUpOutlined
                className="sortButton"
                onClick={() => handleSorting(Field.FolderName, true)}
              />
            </Row>
            <Row>
              <CaretDownOutlined
                className="sortButton"
                onClick={() => handleSorting(Field.FolderName, false)}
              />
            </Row>
          </Col>
        </Row>
      ) : (
        <Col span={23}>
          <p>Folder</p>
        </Col>
      ),
      dataIndex: "folder",
      key: "folder",
      filterDropdown: () => {
        if (isFilteringVisible) {
          return (
            <div style={{ padding: 8 }}>
              <Space direction="vertical" size={16}>
                <Row>
                  <Col>
                    <Tooltip title={'The "*" sign represents any character'}>
                      <Input
                        style={{ color: textColor }}
                        onChange={(event) =>
                          handleAddValueOrPattern(event.target.value, 1)
                        }
                        placeholder="Filter by folder name"
                        defaultValue={filterFields[1].valueOrPattern}
                      ></Input>
                    </Tooltip>
                  </Col>
                </Row>
                <Row>
                  <Col span={10}>
                    <Button
                      className="filterButton"
                      onClick={() => handleResetFiltering([1])}
                    >
                      Reset
                    </Button>
                  </Col>
                  <Col span={14} className="filterOkButtonColumn">
                    <Button
                      className="filterButton"
                      onClick={() =>
                        handleOKFilter(filterFields, sizesWithUnit)
                      }
                    >
                      Filter
                    </Button>
                  </Col>
                </Row>
              </Space>
            </div>
          );
        } else {
          return <></>;
        }
      },
      filterIcon: () => (
        <Button
          type="link"
          icon={<FilterFilled />}
          className={
            isFilteringVisible ? "filter-icon-visible" : "filter-icon-hidden"
          }
        />
      ),
      onCell: (record: any) => {
        return {
          onClick: () => handleOnCellClick(record),
        };
      },
    };
    columns.splice(1, 0, pathColumn);
  }

  const data: DataType[] = dataQuerry.folders
    .map((item, index) => ({
      key: `1.${index}`,
      name: `${item.name}`,
      folder: "",
      size: "",
      modificationDate: "",
      isFolder: true,
    }))
    .concat(
      dataQuerry.files.map((item, index) => ({
        key: `${index}`,
        name: `${item.name}`,
        folder: `${item.folderName}`,
        size: sizeHumanReadable(item.fileSize),
        modificationDate: `${item.modifiedTime}`,
        isFolder: false,
      }))
    );

  const rowSelectionProps: TableRowSelection<DataType> = {
    type: "checkbox",
    selectedRowKeys: selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
    getCheckboxProps: (record: DataType) => ({
      className: "orange-checkbox",
      style: {
        display: record.isFolder ? "none" : "block",
      },
    }),
  };

  return (
    <Col>
      <Modal
        title="Downloading in progress..."
        footer={null}
        closable={false}
        open={downloading}
      >
        <div>
          <Progress percent={percentDone} status="active" size={[400, 15]} />
        </div>
        <span className="avgSpeedSpan">
          {avgSpeed === undefined
            ? ""
            : (avgSpeed / ((1024 * 1024) as number)).toFixed(2) + "MBytes/s"}
        </span>
      </Modal>
      <Modal
        title="Download done!"
        open={percentDone === 100}
        onOk={handleOkDownload}
        cancelButtonProps={{ style: { display: "none" } }}
        closable={false}
      >
        <Progress
          percent={percentDone}
          size={[400, 15]}
          className="percentage"
        />
      </Modal>
      <Row
        className={
          isFilteringVisible ? "resetAllButtonCol" : "resetAllButton-hidden"
        }
      >
        <Col>
          <Button
            className="resetAllButton"
            onClick={() => handleResetFiltering([0, 1, 2, 3, 4, 5])}
          >
            Reset all filters
          </Button>
        </Col>
      </Row>
      <Row>
        <Table
          rowSelection={{ ...rowSelectionProps }}
          columns={columns}
          dataSource={data}
          pagination={false}
          size="small"
          scroll={{ y: `${height}` }}
          className="fileBrowserTable"
        />
      </Row>
      <Row justify="end" gutter={8}>
        <Col>
          <Button
            type="text"
            className="previosButton"
            onClick={() =>
              handlePagingClick(dataQuerry.previousContinuationToken)
            }
            disabled={disable}
            style={{
              visibility: previousTokens.length === 0 ? "hidden" : "visible",
            }}
          >
            &#8592; Previous
          </Button>
        </Col>
        <Col>
          <Button
            type="text"
            className="nextButton"
            onClick={() => handlePagingClick(dataQuerry.nextContinuationToken)}
            disabled={disable}
            style={{
              visibility:
                dataQuerry.moreDataAvaiable === false ? "hidden" : "visible",
            }}
          >
            Next &#8594;
          </Button>
        </Col>
      </Row>
    </Col>
  );
};
